import { parseDateValue, useDateFormat } from '@ben/common/date';
import RiCalendarLine from '@ben/icons/calendar-line';
import type { DateFormat } from '@ben/orion/utils/date';
import type { DatePickerInputProps, DatePickerValue } from '@mantine/dates';
import { DatePickerInput } from '@mantine/dates';
import { format, isValid } from 'date-fns';
import { memo, useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import InputFeedback from '../input-feedback';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import RIcon from '../remix-icon';
import { DatePickerInputPopoverStyles } from './date-input.styles';
import {
  getDateInputColor,
  getDateInputMantineStyles,
} from './date-input.utils';

export type DateInputValue = Date | null;
export type DateInputRangeValue = [DateInputValue, DateInputValue];

export type SupportedDatePickerType = 'default' | 'range';

type DateInputOnChange<T extends SupportedDatePickerType> = T extends 'range'
  ? (value: DateInputRangeValue) => void
  : (value: DateInputValue) => void;

export interface DateInputProps<
  T extends SupportedDatePickerType = 'default',
> extends Omit<
  DatePickerInputProps<T>,
  'onChange' | 'value' | 'defaultValue' | 'minDate' | 'maxDate'
> {
  dateFormat?: DateFormat;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  width?: string;
  isOptional?: boolean;
  hintText?: string;
  showLabel?: boolean;
  withinPortal?: boolean;
  value?: T extends 'range' ? DateInputRangeValue : DateInputValue;
  defaultValue?: T extends 'range' ? DateInputRangeValue : DateInputValue;
  minDate?: Date;
  maxDate?: Date;
  onChange?: DateInputOnChange<T>;
}

const toDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  const date = typeof value === 'string' ? parseDateValue(value) : value;
  return isValid(date) ? date : null;
};

const dateToString = (
  value: Date | string | null | undefined
): string | null => {
  const date = toDate(value);
  if (!date) return null;
  return format(date, 'yyyy-MM-dd');
};

const stringToDate = (value: string | null): Date | null => {
  if (!value) return null;
  return toDate(value);
};

const stringRangeToDateRange = (
  value: [string | null, string | null] | undefined
): DateInputRangeValue => {
  if (!value) return [null, null];
  return [stringToDate(value[0]), stringToDate(value[1])];
};

const dateRangeToStringRange = (
  value: DateInputRangeValue | undefined
): [string | null, string | null] | undefined => {
  if (!value) return undefined;
  return [dateToString(value[0]), dateToString(value[1])];
};

const DateInput = <T extends SupportedDatePickerType = 'default'>({
  dateFormat,
  errorMessage,
  warningMessage,
  successMessage,
  width,
  isOptional = false,
  hintText,
  showLabel = true,
  withinPortal = true,
  onChange,
  value,
  defaultValue,
  minDate,
  maxDate,
  ...rest
}: DateInputProps<T>) => {
  const id = useId();
  const { t } = useTranslation();
  const theme = useTheme();
  const { inputFormat } = useDateFormat();
  const effectiveDateFormat = dateFormat ?? inputFormat;
  const labelText = typeof rest.label === 'string' ? rest.label : '';
  const dateInputLabel = `${labelText} - ${effectiveDateFormat} ${
    isOptional ? t('words.optionalInBrackets') : ''
  }`;

  const mantineValue = useMemo(() => {
    if (rest.type === 'range') {
      return dateRangeToStringRange(value as DateInputRangeValue | undefined);
    }
    return dateToString(value as DateInputValue | undefined);
  }, [rest.type, value]);

  const mantineDefaultValue = useMemo(() => {
    if (rest.type === 'range') {
      return dateRangeToStringRange(
        defaultValue as DateInputRangeValue | undefined
      );
    }
    return dateToString(defaultValue as DateInputValue | undefined);
  }, [defaultValue, rest.type]);

  const handleChange = useCallback(
    (nextValue: DatePickerValue<T, string>) => {
      if (!onChange) return;

      if (rest.type === 'range') {
        const rangeValue = nextValue as [string | null, string | null];
        (onChange as (value: DateInputRangeValue) => void)(
          stringRangeToDateRange(rangeValue)
        );
      } else {
        const singleValue = nextValue as string | null;
        (onChange as (value: DateInputValue) => void)(
          stringToDate(singleValue)
        );
      }
    },
    [onChange, rest.type]
  );

  return (
    <div>
      <DatePickerInputPopoverStyles />
      <DatePickerInput<T>
        id={id}
        clearable
        {...rest}
        value={mantineValue as DatePickerValue<T, string>}
        defaultValue={mantineDefaultValue as DatePickerValue<T, string>}
        minDate={dateToString(minDate) ?? undefined}
        maxDate={dateToString(maxDate) ?? undefined}
        onChange={handleChange}
        label={
          showLabel ? (
            <InputLabelWithHint
              name={dateInputLabel}
              hintText={hintText}
              showLabel
              inputId={id}
            />
          ) : undefined
        }
        aria-label={dateInputLabel}
        numberOfColumns={rest.type === 'range' ? 2 : 1}
        valueFormat={effectiveDateFormat}
        leftSection={
          <RIcon
            icon={RiCalendarLine}
            size="md"
            color={getDateInputColor(theme, {
              defaultColor: theme.palette.primary.main,
              disabled: rest.disabled,
              errorMessage,
            })}
          />
        }
        error={!!errorMessage}
        popoverProps={{ zIndex: 1000, withinPortal }}
        styles={getDateInputMantineStyles(theme, {
          width,
          disabled: rest.disabled,
          errorMessage,
        })}
      />
      <InputFeedback
        errorMessage={errorMessage}
        warningMessage={warningMessage}
        successMessage={successMessage}
      />
    </div>
  );
};

export default memo(DateInput) as typeof DateInput;
